import { z } from "zod";

import {
  VerificationResultSchema,
  type LessonActivity,
  type VerificationResult,
} from "./contracts.js";

const EvidenceRefsSchema = z.array(z.string().min(1).max(500)).min(1).max(8);

export const VerificationFactsSchema = z.discriminatedUnion("outcome", [
  z
    .object({
      outcome: z.literal("confirmed_correct"),
      evidenceRefs: EvidenceRefsSchema,
    })
    .strict(),
  z
    .object({
      outcome: z.literal("confirmed_incorrect"),
      evidenceRefs: EvidenceRefsSchema,
      contradiction: z.string().min(1).max(1_000),
    })
    .strict(),
  z
    .object({
      outcome: z.literal("unresolved"),
      evidenceRefs: EvidenceRefsSchema,
      reason: z.string().min(1).max(1_000),
    })
    .strict(),
]);
export type VerificationFacts = z.infer<typeof VerificationFactsSchema>;

export const FormativeTeacherProposalSchema = z
  .object({
    assessment: z.enum([
      "understood",
      "partly_understood",
      "misunderstood",
      "unclear",
    ]),
    nextAction: z.enum(["stay", "clarify", "advance"]),
    evidenceQuote: z.string().min(1).max(240).optional(),
  })
  .strict();
export type FormativeTeacherProposal = z.infer<
  typeof FormativeTeacherProposalSchema
>;

export const RemediationContextSchema = z
  .object({
    sourceActivityId: z.string().min(1),
    targetActivityId: z.string().min(1),
    sourceMathematicalObject: z.string().min(1).max(1_000),
    targetMathematicalObject: z.string().min(1).max(1_000),
    studentAnswerExcerpt: z.string().min(1).max(1_000),
    verificationFacts: VerificationFactsSchema,
    sourceActivityRecentTurns: z.array(z.string().min(1).max(1_000)).max(8),
  })
  .strict();
export type RemediationContext = z.infer<typeof RemediationContextSchema>;

export const IndependentEvidenceReviewStateSchema = z
  .object({
    status: z.enum([
      "verified_correct",
      "verified_incorrect",
      "independent_under_review",
    ]),
    frozenAnswerRef: z.string().min(1).max(500),
    verificationFacts: VerificationFactsSchema,
  })
  .strict();
export type IndependentEvidenceReviewState = z.infer<
  typeof IndependentEvidenceReviewStateSchema
>;

export const FormativeProposalAuditSchema = z.discriminatedUnion("decision", [
  z
    .object({
      decision: z.literal("accepted"),
      action: FormativeTeacherProposalSchema.shape.nextAction,
      reasonCode: z.literal("proposal_within_authority"),
    })
    .strict(),
  z
    .object({
      decision: z.literal("rejected"),
      action: z.enum(["stay", "clarify"]),
      reasonCode: z.enum([
        "independent_boundary",
        "confirmed_incorrect_conflict",
        "explicit_student_uncertainty",
        "assessment_action_conflict",
        "missing_evidence_quote",
        "evidence_quote_not_from_current_answer",
        "no_legal_next_activity",
      ]),
    })
    .strict(),
]);
export type FormativeProposalAudit = z.infer<
  typeof FormativeProposalAuditSchema
>;

const TeacherAuthorityShadowBaseSchema = z.object({
  schemaVersion: z.literal("1"),
  mode: z.enum(["observe_only", "active"]),
  sourceActivityId: z.string().min(1),
  sourceIndependence: z.enum(["teacher_led", "guided", "independent"]),
  verificationFacts: VerificationFactsSchema,
  legacyDecision: z
    .object({
      kind: z.enum(["stay", "advance", "remediate", "complete"]),
      targetActivityId: z.string().min(1),
      teacherSupportedAdvance: z.boolean(),
    })
    .strict(),
  appliedDecision: z
    .object({
      kind: z.enum(["stay", "advance", "remediate", "complete"]),
      targetActivityId: z.string().min(1),
      teacherSupportedAdvance: z.boolean(),
    })
    .strict()
    .optional(),
});

export const TeacherAuthorityShadowRecordSchema = z.discriminatedUnion(
  "status",
  [
    TeacherAuthorityShadowBaseSchema.extend({
      status: z.literal("proposal_audited"),
      proposal: FormativeTeacherProposalSchema,
      audit: FormativeProposalAuditSchema,
    }).strict(),
    TeacherAuthorityShadowBaseSchema.extend({
      status: z.literal("proposal_missing"),
    }).strict(),
    TeacherAuthorityShadowBaseSchema.extend({
      status: z.literal("not_applicable_independent"),
    }).strict(),
    TeacherAuthorityShadowBaseSchema.extend({
      status: z.literal("teacher_unavailable"),
    }).strict(),
  ],
);
export type TeacherAuthorityShadowRecord = z.infer<
  typeof TeacherAuthorityShadowRecordSchema
>;

export function verificationFactsFromResult(input: {
  verification: VerificationResult;
  evidenceRef: string;
  confirmedContradiction?: string;
}): VerificationFacts {
  const verification = VerificationResultSchema.parse(input.verification);
  const evidenceRefs = EvidenceRefsSchema.parse([input.evidenceRef]);
  const contradiction = input.confirmedContradiction?.trim();
  if (verification.outcome === "correct") {
    if (contradiction) {
      throw new Error("A correct verification cannot carry a confirmed contradiction");
    }
    return VerificationFactsSchema.parse({
      outcome: "confirmed_correct",
      evidenceRefs,
    });
  }
  if (contradiction) {
    return VerificationFactsSchema.parse({
      outcome: "confirmed_incorrect",
      evidenceRefs,
      contradiction,
    });
  }
  return VerificationFactsSchema.parse({
    outcome: "unresolved",
    evidenceRefs,
    reason:
      verification.reasons.filter(Boolean).join("；").slice(0, 1_000) ||
      "Legacy verification did not establish a hard mathematical fact",
  });
}

export function auditFormativeTeacherProposal(input: {
  activity: Pick<LessonActivity, "id" | "independence">;
  verificationFacts: VerificationFacts;
  proposal: FormativeTeacherProposal;
  studentAnswerText: string;
  hasNextActivity: boolean;
  nextActivityIndependence?: LessonActivity["independence"] | undefined;
}): FormativeProposalAudit {
  const facts = VerificationFactsSchema.parse(input.verificationFacts);
  const proposal = FormativeTeacherProposalSchema.parse(input.proposal);
  if (input.activity.independence === "independent") {
    return {
      decision: "rejected",
      action: "stay",
      reasonCode: "independent_boundary",
    };
  }
  if (
    proposal.assessment === "understood" &&
    proposal.nextAction === "clarify"
  ) {
    return {
      decision: "rejected",
      action: "clarify",
      reasonCode: "assessment_action_conflict",
    };
  }
  if (proposal.nextAction !== "advance") {
    return {
      decision: "accepted",
      action: proposal.nextAction,
      reasonCode: "proposal_within_authority",
    };
  }
  if (facts.outcome === "confirmed_incorrect") {
    return {
      decision: "rejected",
      action: "clarify",
      reasonCode: "confirmed_incorrect_conflict",
    };
  }
  if (!input.hasNextActivity) {
    return {
      decision: "rejected",
      action: "stay",
      reasonCode: "no_legal_next_activity",
    };
  }
  // Moving to more teaching is not a claim of mastery or entry to independent work.
  const continuesTeaching = input.nextActivityIndependence === "teacher_led" ||
    input.nextActivityIndependence === "guided";
  if (proposal.assessment !== "understood" && !continuesTeaching) {
    return {
      decision: "rejected",
      action: "clarify",
      reasonCode: "assessment_action_conflict",
    };
  }
  if (studentReportsPersonalUncertainty(input.studentAnswerText)) {
    return {
      decision: "rejected",
      action: "clarify",
      reasonCode: "explicit_student_uncertainty",
    };
  }
  if (facts.outcome === "unresolved") {
    const quote = proposal.evidenceQuote;
    if (!quote) {
      return {
        decision: "rejected",
        action: "clarify",
        reasonCode: "missing_evidence_quote",
      };
    }
    const normalizedQuote = normalizeEvidenceText(quote);
    const normalizedAnswer = normalizeEvidenceText(input.studentAnswerText);
    if (!normalizedQuote || !normalizedAnswer.includes(normalizedQuote)) {
      return {
        decision: "rejected",
        action: "clarify",
        reasonCode: "evidence_quote_not_from_current_answer",
      };
    }
  }
  return {
    decision: "accepted",
    action: "advance",
    reasonCode: "proposal_within_authority",
  };
}

export function studentReportsPersonalUncertainty(value: string): boolean {
  const normalized = normalizeEvidenceText(value);
  const explicitEpistemicGap =
    /(?:不知道|不清楚|不明白|没明白|没看懂|没弄懂|答不上来|想不出来|拿不准|说不准|没(?:有)?把握|不(?:太|怎么|很)?确定[^。；!?！？\n]{0,16}(?:能不能|该|要|怎么|为什么|哪里|哪一步))/u;
  const personalShortGap =
    /(?:(?:我|自己|这(?:里|一步|个地方))[^。；!?！？\n]{0,16}(?:不(?:太)?懂|不会|不确定)|不确定[^。；!?！？\n]{0,16}(?:这样写|写得?|答得?|答案|结论)[^。；!?！？\n]{0,8}(?:对不对|是否正确|有没有错))/u;
  return (
    explicitEpistemicGap.test(normalized) || personalShortGap.test(normalized)
  );
}

function normalizeEvidenceText(value: string): string {
  return value.normalize("NFKC").replace(/\s+/gu, " ").trim();
}

export function independentEvidenceReviewState(input: {
  verificationFacts: VerificationFacts;
  frozenAnswerRef: string;
}): IndependentEvidenceReviewState {
  const facts = VerificationFactsSchema.parse(input.verificationFacts);
  const status =
    facts.outcome === "confirmed_correct"
      ? "verified_correct"
      : facts.outcome === "confirmed_incorrect"
        ? "verified_incorrect"
        : "independent_under_review";
  return IndependentEvidenceReviewStateSchema.parse({
    status,
    frozenAnswerRef: input.frozenAnswerRef,
    verificationFacts: facts,
  });
}
